import { useState } from 'react';
import { Button, Input, Modal, ModalContent, ModalHeader } from '@strivelabs/ui-core/v2';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { CHAT_TITLE_MAX_LENGTH, titleOf } from './chatTitle';

export interface RenameDialogProps {
  execution: WorkflowExecution | null;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
}

interface RenameFormProps {
  execution: WorkflowExecution;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
}

/**
 * The form itself, seeded once per conversation.
 *
 * Keyed on the execution id by its parent so opening a different conversation
 * remounts it with a fresh draft. That replaces an effect that copied the title
 * into state on every change of `execution` — derived state that had to be kept
 * in sync by hand, and briefly rendered the previous chat's title.
 */
function RenameForm({ execution, isSaving, onCancel, onSave }: RenameFormProps) {
  const [draft, setDraft] = useState(execution.title ?? '');

  return (
    <Modal open onOpenChange={next => !next && onCancel()}>
      <ModalContent className="w-[420px] p-0" ariaLabel="Rename chat">
        <ModalHeader title="Rename chat" description="Leave this empty to restore the automatic title." />
        <form
          onSubmit={event => {
            event.preventDefault();
            onSave(draft);
          }}
          className="px-6 pb-6"
        >
          <Input
            value={draft}
            onChange={setDraft}
            maxLength={CHAT_TITLE_MAX_LENGTH}
            autoFocus
            aria-label="Chat title"
            placeholder={titleOf(execution)}
          />

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="borderless" size="sm" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" type="submit" state={isSaving ? 'loading' : undefined}>
              Save
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}

/** Rename a conversation. Submitting an empty value clears back to the derived title. */
export function RenameDialog({ execution, isSaving, onCancel, onSave }: RenameDialogProps) {
  if (!execution) return null;

  return (
    <RenameForm
      key={execution.id}
      execution={execution}
      isSaving={isSaving}
      onCancel={onCancel}
      onSave={onSave}
    />
  );
}
